// context/NotificationContext.tsx
import { createContext, useContext, useState, useCallback, ReactNode, JSX } from 'react';
import { Snackbar, Alert, Slide, SlideProps } from '@mui/material';
import { AlertColor } from '@mui/material/Alert';

// Types
export interface NotificationOptions {
  message?: string;
  severity?: AlertColor;
  duration?: number;
  title?: string;
}

interface Notification {
  id: number;
  message: string;
  severity: AlertColor;
  duration: number;
  title?: string;
  open: boolean;
}

export interface NotificationContextValue {
  showNotification: (options: NotificationOptions | string) => number;
  closeNotification: (id: number) => void;
  success: (message: string, options?: Partial<NotificationOptions>) => number;
  error: (message: string, options?: Partial<NotificationOptions>) => number;
  warning: (message: string, options?: Partial<NotificationOptions>) => number;
  info: (message: string, options?: Partial<NotificationOptions>) => number;
  clearAll: () => void;
}

export interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

// Transition component
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

/**
 * NotificationProvider - Manages toast notifications across the app
 */
export function NotificationProvider({ children }: NotificationProviderProps): JSX.Element {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  /**
   * Show a notification
   */
  const showNotification = useCallback((options: NotificationOptions | string): number => {
    const id = Date.now() + Math.random();
    const notification: Notification = {
      id,
      message: typeof options === 'string' ? options : (options.message || ''),
      severity: typeof options === 'string' ? 'info' : (options.severity || 'info'),
      duration: typeof options === 'string' ? 4000 : (options.duration || 4000),
      title: typeof options === 'string' ? undefined : options.title,
      open: true,
    };

    setNotifications((prev) => [...prev, notification]);

    return id;
  }, []);

  /**
   * Close a specific notification
   */
  const closeNotification = useCallback((id: number): void => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, open: false } : n))
    );

    // Remove from array after animation
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 300);
  }, []);

  /**
   * Shorthand methods
   */
  const success = useCallback((message: string, options: Partial<NotificationOptions> = {}): number => {
    return showNotification({ message, severity: 'success', ...options });
  }, [showNotification]);

  const error = useCallback((message: string, options: Partial<NotificationOptions> = {}): number => {
    return showNotification({ message, severity: 'error', duration: 6000, ...options });
  }, [showNotification]);

  const warning = useCallback((message: string, options: Partial<NotificationOptions> = {}): number => {
    return showNotification({ message, severity: 'warning', ...options });
  }, [showNotification]);

  const info = useCallback((message: string, options: Partial<NotificationOptions> = {}): number => {
    return showNotification({ message, severity: 'info', ...options });
  }, [showNotification]);

  /**
   * Clear all notifications
   */
  const clearAll = useCallback((): void => {
    setNotifications([]);
  }, []);

  const value: NotificationContextValue = {
    showNotification,
    closeNotification,
    success,
    error,
    warning,
    info,
    clearAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {/* Render notifications */}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={notification.open}
          autoHideDuration={notification.duration}
          onClose={() => closeNotification(notification.id)}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            bottom: { xs: 16 + index * 60, sm: 24 + index * 70 },
          }}
        >
          <Alert
            onClose={() => closeNotification(notification.id)}
            severity={notification.severity}
            variant="filled"
            sx={{
              width: '100%',
              minWidth: 300,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.1)',

              // Custom colors based on severity
              ...(notification.severity === 'success' && {
                backgroundColor: 'rgba(0, 255, 136, 0.9)',
                color: '#0A0E14',
                '& .MuiAlert-icon': { color: '#0A0E14' },
              }),
              ...(notification.severity === 'error' && {
                backgroundColor: 'rgba(255, 51, 102, 0.9)',
                color: '#FFFFFF',
              }),
              ...(notification.severity === 'warning' && {
                backgroundColor: 'rgba(255, 230, 0, 0.9)',
                color: '#0A0E14',
                '& .MuiAlert-icon': { color: '#0A0E14' },
              }),
              ...(notification.severity === 'info' && {
                backgroundColor: 'rgba(0, 255, 212, 0.9)',
                color: '#0A0E14',
                '& .MuiAlert-icon': { color: '#0A0E14' },
              }),
            }}
          >
            {notification.title && (
              <strong style={{ display: 'block', marginBottom: 4 }}>
                {notification.title}
              </strong>
            )}
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
}

/**
 * useNotification hook - Access notification methods
 */
export function useNotification(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
